import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { CatalogApi } from '@backstage/catalog-client';
import { useApi, toastApiRef, ToastApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Project,
  ProjectEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  ProvidersApi,
  providersApiRef,
} from '@internal/backstage-plugin-catalog-devops-react';
import { AdminApi, adminApiRef } from '../apis';
import { ActionTypes } from './types';

interface ProjectsContextProviderProps {
  children: ReactNode;
}

type LoadCompletedProjectsAction = {
  type: ActionTypes.LOAD_COMPLETED;
  payload: ProjectEntity[];
};

type DeleteProjectAction = {
  type: ActionTypes.DELETE;
};

type DeleteCompletedProjectAction = {
  type: ActionTypes.DELETE_COMPLETED;
};

type CreateCompletedProjectAction = {
  type: ActionTypes.CREATE_COMPLETED;
};

type EditCompletedProjectAction = {
  type: ActionTypes.EDIT_COMPLETED;
};
type CreateProjectAction = {
  type: ActionTypes.CREATE;
  payload: Project;
};

type EditProjectAction = {
  type: ActionTypes.EDIT;
  payload: Project;
};

type SelectProjectAction = {
  type: ActionTypes.SELECT;
  payload: ProjectEntity;
};

type UnselectProjectAction = {
  type: ActionTypes.UNSELECT;
};

type ProjectsAction =
  | LoadCompletedProjectsAction
  | DeleteProjectAction
  | CreateProjectAction
  | EditProjectAction
  | SelectProjectAction
  | UnselectProjectAction
  | DeleteCompletedProjectAction
  | CreateCompletedProjectAction
  | EditCompletedProjectAction;

type ProjectsState = {
  projects: ProjectEntity[];
  selectedProject: ProjectEntity | undefined;
  status: string;
  newProject: Project | undefined;
};

type ProjectsContextType = {
  projects: ProjectEntity[];
  selectedProject: ProjectEntity | undefined;
  isLoading: boolean;
  deleteProject(): void;
  createProject(project: Project): void;
  editProject(project: Project): void;
  selectProject(project: ProjectEntity): void;
  unselectProject(): void;
};

const initialProjectsState = {
  projects: [],
  selectedProject: undefined,
  status: 'initial_load',
  newProject: undefined,
};

export const ProjectsContext = createContext<ProjectsContextType | null>(null);

export function ProjectsContextProvider(
  props: Readonly<ProjectsContextProviderProps>,
) {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const adminApi = useApi<AdminApi>(adminApiRef);
  const providersApi = useApi<ProvidersApi>(providersApiRef);
  const toastApi = useApi<ToastApi>(toastApiRef);

  const projectsReducer = (
    state: ProjectsState,
    action: ProjectsAction,
  ): ProjectsState => {
    switch (action.type) {
      case ActionTypes.LOAD_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.projects = action.payload;
        stateCopy.status = 'wait';
        return stateCopy;
      }
      case ActionTypes.DELETE: {
        const stateCopy = { ...state };
        stateCopy.status = 'delete';
        return stateCopy;
      }
      case ActionTypes.DELETE_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.selectedProject = undefined;
        stateCopy.status = 'load';
        return stateCopy;
      }
      case ActionTypes.CREATE: {
        const stateCopy = { ...state };
        stateCopy.status = 'create';
        stateCopy.newProject = action.payload;
        return stateCopy;
      }
      case ActionTypes.CREATE_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.status = 'load';
        stateCopy.newProject = undefined;
        return stateCopy;
      }
      case ActionTypes.EDIT: {
        const stateCopy = { ...state };
        stateCopy.newProject = action.payload;
        stateCopy.status = 'edit';
        return stateCopy;
      }
      case ActionTypes.EDIT_COMPLETED: {
        const stateCopy = { ...state };
        stateCopy.newProject = undefined;
        stateCopy.selectedProject = undefined;
        stateCopy.status = 'load';
        return stateCopy;
      }
      case ActionTypes.SELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedProject = action.payload;
        return stateCopy;
      }
      case ActionTypes.UNSELECT: {
        const stateCopy = { ...state };
        stateCopy.selectedProject = undefined;
        return stateCopy;
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(projectsReducer, initialProjectsState);

  useEffect(() => {
    const projectFilter = { kind: 'Domain' };
    if (state.status === 'initial_load') {
      catalogApi.getEntities({ filter: projectFilter }).then(value => {
        dispatch({
          type: ActionTypes.LOAD_COMPLETED,
          payload: value.items,
        });
      });
    }

    if (state.status === 'load') {
      new Promise(resolve => setTimeout(resolve, 2000)).then(() => {
        catalogApi.getEntities({ filter: projectFilter }).then(value => {
          dispatch({
            type: ActionTypes.LOAD_COMPLETED,
            payload: value.items,
          });
        });
      });
    }
  }, [catalogApi, state.status]);

  useEffect(() => {
    const createEntity = async () => {
      if (state.status !== 'create') return;
      if (state.newProject === undefined) return;

      const projectResponse = await adminApi.createProjectEntity(
        state.newProject,
      );

      if (projectResponse.succeeded) {
        await providersApi.triggerProvider('Domain');
        toastApi.post({
          title: `Project "${state.newProject.ProjectId}" was created successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while creating project.`,
          description: projectResponse.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      const groupResponse = await adminApi.createGroupEntity({
        GroupId: `${state.newProject.ProjectId}_owners`,
        Description: `${state.newProject.Title} Project Owners Group`,
        DisplayName: `${state.newProject.Title} Owners`,
      });

      if (groupResponse.succeeded) {
        await providersApi.triggerProvider('Group');
        toastApi.post({
          title: `Group "${state.newProject.ProjectId}_owners" was created successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while creating group.`,
          description: groupResponse.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.CREATE_COMPLETED });
    };

    createEntity();
  }, [adminApi, providersApi, toastApi, state.status, state.newProject]);

  useEffect(() => {
    const editEntity = async () => {
      if (state.status !== 'edit') return;
      if (state.newProject === undefined) return;
      if (state.selectedProject === undefined) return;

      const response = await adminApi.editProjectEntity(
        state.selectedProject.spec?.dynamic_uuid as string,
        state.newProject,
      );

      if (response.succeeded) {
        await providersApi.triggerProvider('Domain');
        toastApi.post({
          title: `Project "${state.newProject.ProjectId}" was updated successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while updating project.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.EDIT_COMPLETED });
    };

    editEntity();
  }, [
    adminApi,
    providersApi,
    toastApi,
    state.status,
    state.selectedProject,
    state.newProject,
  ]);

  useEffect(() => {
    const deleteEntity = async () => {
      if (state.status !== 'delete') return;
      if (state.selectedProject === undefined) return;

      const response = await adminApi.deleteProjectEntity(
        state.selectedProject.spec?.dynamic_uuid as string,
      );

      if (response.succeeded) {
        await providersApi.triggerProvider('Domain');
        toastApi.post({
          title: `Project "${state.selectedProject.metadata.name}" was deleted successfully.`,
          status: 'success',
          timeout: 5000,
        });
      } else {
        toastApi.post({
          title: `An error occurred while deleting project.`,
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }

      dispatch({ type: ActionTypes.DELETE_COMPLETED });
    };

    deleteEntity();
  }, [adminApi, providersApi, toastApi, state.status, state.selectedProject]);

  const deleteProject = () => dispatch({ type: ActionTypes.DELETE });
  const createProject = (project: Project) =>
    dispatch({ type: ActionTypes.CREATE, payload: project });
  const editProject = (project: Project) =>
    dispatch({ type: ActionTypes.EDIT, payload: project });
  const selectProject = (project: ProjectEntity) =>
    dispatch({ type: ActionTypes.SELECT, payload: project });
  const unselectProject = () => dispatch({ type: ActionTypes.UNSELECT });

  const context = useMemo(
    () => ({
      projects: state.projects,
      selectedProject: state.selectedProject,
      isLoading: state.status !== 'wait',
      deleteProject,
      createProject,
      editProject,
      selectProject,
      unselectProject,
    }),
    [state.projects, state.selectedProject, state.status],
  );

  return (
    <ProjectsContext.Provider value={context}>
      {props.children}
    </ProjectsContext.Provider>
  );
}
